/* eslint-disable react/no-unescaped-entities */
import { useEffect, useRef, useState } from "react";
import { Wind, Eye, Waves, Sparkles, X, Pause, Play } from "lucide-react";

const KARAKIA = [
  {
    title: "Karakia tīmatanga · before you begin",
    body: `Kia tau ngā manaakitanga
o te wāhi ngaro ki runga i tēnei rā,
ki runga i a tātou katoa.
Tihei mauri ora.`,
  },
  {
    title: "Whakatau wairua · settle the wairua",
    body: `Tukua kia kau, kia rere
te wai o tēnei rangi.
Ko au, kei konei.
Ko au, kei te whenua.
Tihei mauri ora.`,
  },
];

export default function Toolkit() {
  const [active, setActive] = useState(null);

  return (
    <div className="space-y-8 animate-fadeup">
      <header>
        <div className="text-[11px] uppercase tracking-[0.22em] text-kokowai mb-2">Hā · the toolkit</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-charcoal mb-2">Tools for the moment</h1>
        <p className="text-charcoal/70 max-w-2xl">
          When the wave hits — short, real practices that fit in your hand. Pick one. Use it for two minutes. Move on.
        </p>
      </header>

      <div className="grid sm:grid-cols-2 gap-4">
        <ToolCard
          testid="tool-breathing"
          icon={Wind}
          title="Box breathing"
          body="4 in · 4 hold · 4 out · 4 hold. Two minutes."
          onClick={() => setActive("breath")}
        />
        <ToolCard
          testid="tool-grounding"
          icon={Eye}
          title="5-4-3-2-1 grounding"
          body="Five senses, in order. Bring yourself back into the room."
          onClick={() => setActive("ground")}
        />
        <ToolCard
          testid="tool-urge"
          icon={Waves}
          title="Urge surfing"
          body="The urge is a wave. Three minutes — watch it rise, peak, fall."
          onClick={() => setActive("urge")}
        />
        <ToolCard
          testid="tool-karakia"
          icon={Sparkles}
          title="Karakia"
          body="Two short karakia — to settle, to begin."
          onClick={() => setActive("karakia")}
        />
      </div>

      {active === "breath" && <Modal onClose={() => setActive(null)} title="Box breathing" testid="tool-breathing-modal"><Breathing /></Modal>}
      {active === "ground" && <Modal onClose={() => setActive(null)} title="5-4-3-2-1" testid="tool-grounding-modal"><Grounding /></Modal>}
      {active === "urge" && <Modal onClose={() => setActive(null)} title="Urge surfing" testid="tool-urge-modal"><UrgeSurf /></Modal>}
      {active === "karakia" && <Modal onClose={() => setActive(null)} title="Karakia" testid="tool-karakia-modal"><Karakia /></Modal>}
    </div>
  );
}

function ToolCard({ icon: Icon, title, body, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="bg-muka rounded-2xl p-6 sm:p-7 border border-border hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(42,42,42,0.08)] transition-all text-left"
    >
      <Icon size={24} strokeWidth={1.4} className="text-kokowai mb-3" />
      <h3 className="font-serif text-xl text-charcoal mb-1">{title}</h3>
      <p className="text-sm text-charcoal/70">{body}</p>
    </button>
  );
}

function Modal({ children, title, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-charcoal/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeup" data-testid={testid}>
      <div className="bg-muka rounded-3xl p-6 sm:p-9 max-w-lg w-full border border-border relative">
        <button
          onClick={onClose}
          data-testid="tool-modal-close"
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-bone hover:bg-clay/30 flex items-center justify-center"
          aria-label="Close"
        >
          <X size={16} />
        </button>
        <div className="text-[11px] uppercase tracking-[0.22em] text-kokowai mb-2">{title}</div>
        {children}
      </div>
    </div>
  );
}

function breathScale(phase, smallValue) {
  // phase: 0=in (expand), 1=hold-full, 2=out (contract), 3=hold-empty
  if (phase === 0 || phase === 1) return 1;
  return smallValue;
}

function Breathing() {
  const [running, setRunning] = useState(true);
  const phases = ["Hā mai · in", "Tāria · hold", "Hā atu · out", "Tāria · hold"];
  const [phase, setPhase] = useState(0);
  const [count, setCount] = useState(4);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      setCount((c) => {
        if (c <= 1) {
          setPhase((p) => (p + 1) % 4);
          return 4;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [running]);

  return (
    <div className="flex flex-col items-center py-6">
      <div className="relative w-56 h-56 mb-6 flex items-center justify-center">
        <div
          className="absolute inset-0 rounded-full bg-ngahere/15 transition-all duration-1000 ease-in-out"
          style={{ transform: `scale(${breathScale(phase, 0.6)})` }}
        />
        <div
          className="absolute inset-6 rounded-full bg-ngahere/25 transition-all duration-1000 ease-in-out"
          style={{ transform: `scale(${breathScale(phase, 0.55)})` }}
        />
        <div className="relative font-serif text-6xl text-ngahere">{count}</div>
      </div>
      <div className="font-serif text-2xl text-charcoal mb-1">{phases[phase]}</div>
      <p className="text-xs text-muted-foreground mb-5">Slow. There's no rush.</p>
      <button
        onClick={() => setRunning(!running)}
        data-testid="breathing-toggle"
        className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-border hover:bg-bone"
      >
        {running ? <><Pause size={16} /> pause</> : <><Play size={16} /> resume</>}
      </button>
    </div>
  );
}

function Grounding() {
  const steps = [
    "Five things you can see",
    "Four things you can touch",
    "Three things you can hear",
    "Two things you can smell",
    "One thing you can taste",
  ];
  const [i, setI] = useState(0);
  return (
    <div className="py-4">
      <div className="font-serif text-3xl text-ngahere mb-3">{steps[i]}</div>
      <p className="text-charcoal/70 mb-6 text-sm">
        Slowly. Out loud if you can. Pause between each.
      </p>
      <div className="flex justify-between items-center">
        <div className="text-xs text-muted-foreground">{i + 1} / {steps.length}</div>
        <div className="flex gap-2">
          {i > 0 && (
            <button
              onClick={() => setI(i - 1)}
              data-testid="grounding-back"
              className="px-4 py-2.5 rounded-full border border-border hover:bg-bone"
            >back</button>
          )}
          <button
            onClick={() => setI(Math.min(i + 1, steps.length - 1))}
            disabled={i === steps.length - 1}
            data-testid="grounding-next"
            className="px-5 py-2.5 rounded-full bg-ngahere text-muka disabled:opacity-50"
          >
            {i === steps.length - 1 ? "ka pai" : "next"}
          </button>
        </div>
      </div>
    </div>
  );
}

function UrgeSurf() {
  const [secs, setSecs] = useState(0);
  const [running, setRunning] = useState(true);
  const ref = useRef();
  useEffect(() => {
    if (!running) return;
    ref.current = setInterval(() => setSecs((s) => Math.min(s + 1, 180)), 1000);
    return () => clearInterval(ref.current);
  }, [running]);

  const pct = (secs / 180) * 100;

  return (
    <div className="py-2">
      <p className="text-charcoal/80 text-sm leading-relaxed mb-5">
        The urge is a wave. It rises, it peaks, it falls — every time. Stay with it for three minutes.
        Notice the body. Notice the thought. You don't have to act.
      </p>
      <div className="bg-bone rounded-2xl p-6 mb-4">
        <div className="font-serif text-5xl text-ngahere text-center mb-2 tabular-nums">
          {Math.floor(secs / 60)}:{String(secs % 60).padStart(2, "0")}
        </div>
        <div className="h-2 rounded-full bg-border overflow-hidden">
          <div className="h-full bg-ngahere transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
        <div className="text-xs text-center text-muted-foreground mt-2">
          {secs >= 180 ? "Ka pai. The wave passed." : "Watching the wave..."}
        </div>
      </div>
      <button
        onClick={() => setRunning(!running)}
        data-testid="urge-toggle"
        className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-border hover:bg-bone"
      >
        {running ? <><Pause size={16} /> pause</> : <><Play size={16} /> resume</>}
      </button>
    </div>
  );
}

function Karakia() {
  return (
    <div className="space-y-5 py-2">
      {KARAKIA.map((k, i) => (
        <div key={i} className="bg-bone rounded-2xl p-5 border border-border">
          <div className="text-[11px] uppercase tracking-[0.18em] text-kokowai mb-2">{k.title}</div>
          <p className="font-serif text-lg text-charcoal whitespace-pre-line leading-relaxed italic">
            {k.body}
          </p>
        </div>
      ))}
    </div>
  );
}